import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { User, Globe, Loader2, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const { t, language } = useTranslation();
  const { user, updateUser, changeLanguage } = useAuth();
  const [saving, setSaving] = useState(false);
  
  const [profile, setProfile] = useState({
    name: user?.name || '',
    department: user?.department || '',
    phone: user?.phone || ''
  });

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/auth/profile`, profile);
      updateUser(response.data);
      toast.success(t('profileUpdated'));
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleLanguageChange = async (lang) => {
    changeLanguage(lang);
    try {
      const response = await axios.put(`${API}/auth/profile`, { language: lang });
      updateUser(response.data);
    } catch (error) {
      console.error('Failed to save language:', error);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="settings-page">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 font-[Manrope]">{t('settings')}</h2>
        <p className="text-slate-500 mt-1 text-sm">Verwalten Sie Ihr Profil und Einstellungen</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList className="w-full sm:w-auto grid grid-cols-2 sm:flex">
          <TabsTrigger value="profile" data-testid="profile-tab" className="text-xs sm:text-sm">
            <User className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{t('profile')}</span>
          </TabsTrigger>
          <TabsTrigger value="language" data-testid="language-tab" className="text-xs sm:text-sm">
            <Globe className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{t('language')}</span>
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="bg-white border border-slate-200">
            <CardHeader>
              <CardTitle className="font-[Manrope]">{t('profile')}</CardTitle>
              <CardDescription>Aktualisieren Sie Ihre persönlichen Informationen</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-[#D92632] flex items-center justify-center text-white text-2xl font-semibold">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div>
                  <p className="font-medium text-slate-900">{user?.email}</p>
                  <Badge className="mt-1">{t(user?.role)}</Badge>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    className="mt-1"
                    data-testid="profile-name-input"
                  />
                </div>
                <div>
                  <Label>Abteilung</Label>
                  <Input
                    value={profile.department}
                    onChange={(e) => setProfile({ ...profile, department: e.target.value })}
                    placeholder="z.B. Marketing"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>{t('phone')}</Label>
                  <Input
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    placeholder="+49 123 456789"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="flex justify-end pt-4">
                <Button 
                  onClick={handleSaveProfile}
                  className="bg-[#D92632] hover:bg-[#B91C26]"
                  disabled={saving}
                  data-testid="save-profile-btn"
                >
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t('save')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Language Tab */}
        <TabsContent value="language">
          <Card className="bg-white border border-slate-200">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="font-[Manrope] text-base sm:text-lg">{t('language')}</CardTitle>
              <CardDescription className="text-sm">Wählen Sie Ihre bevorzugte Sprache</CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                {[
                  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
                  { code: 'en', label: 'English', flag: '🇬🇧' },
                  { code: 'fr', label: 'Français', flag: '🇫🇷' }
                ].map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`p-3 sm:p-4 rounded-xl border-2 transition-all ${
                      language === lang.code 
                        ? 'border-[#D92632] bg-red-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`lang-option-${lang.code}`}
                  >
                    <span className="text-2xl sm:text-3xl">{lang.flag}</span>
                    <p className="font-medium text-slate-900 mt-2 text-xs sm:text-base">{lang.label}</p>
                    {language === lang.code && (
                      <Check className="h-4 w-4 sm:h-5 sm:w-5 text-[#D92632] mx-auto mt-2" />
                    )}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
